import {
  parsePhoneNumberFromString,
  getCountryCallingCode,
  type CountryCode,
} from 'libphonenumber-js/max';

/**
 * Normalizes a phone number to E.164 format (e.g., +1234567890).
 *
 * When `defaultCountry` (ISO 3166-1 alpha-2) is provided, libphonenumber-js
 * handles country-specific quirks: stripping national trunk prefixes for
 * countries like GB ("07911..." → "+44..."), preserving leading zeros where
 * they are part of E.164 (e.g. Italian landlines: "+39 06 ..."), etc.
 *
 * If the parser cannot interpret the input, falls back to a permissive
 * best-effort: trust the input rather than reject. No arbitrary length cap.
 *
 * @param phoneNumber - Phone number in any common format
 * @param defaultCountry - ISO alpha-2 country code (e.g. "IN", "GB"). Optional.
 */
export function normalizeToE164(
  phoneNumber: string | null | undefined,
  defaultCountry?: string | null
): string | null {
  if (!phoneNumber) return null;
  const trimmed = phoneNumber.trim();
  if (!trimmed) return null;

  const country = (defaultCountry || undefined) as CountryCode | undefined;
  // No defaultCountry → fall back to 'US' to preserve legacy US-default
  // behaviour (10-digit → +1, etc.). Inputs already in E.164 are parsed
  // correctly regardless of the country hint.
  const parsed = parsePhoneNumberFromString(trimmed, country ?? 'US');
  if (parsed) return parsed.number;

  const cleaned = trimmed.replace(/[\s\-().]/g, '');
  if (cleaned.startsWith('+')) {
    const digits = cleaned.slice(1).replace(/\D/g, '');
    return digits ? `+${digits}` : null;
  }
  const digits = cleaned.replace(/\D/g, '');
  if (!digits) return null;

  if (country) {
    try {
      const dial = getCountryCallingCode(country);
      return `+${dial}${digits.replace(/^0+/, '')}`;
    } catch {
      /* invalid country code — fall through */
    }
  }
  return `+${digits}`;
}
